/* eslint-disable no-console */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CheckIcon from '@material-ui/icons/CheckCircleOutline';
import {
  Box,
  Button,
  Collapse,
  Container,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  makeStyles,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Footer } from '../components/Footer';
import { UploadIcon } from '../components/atoms/UploadIcon';
import { FileIcon } from '../components/atoms/FileIcon';
import CatalogoService from '../API/CatalogoService';
import { InputControlled } from '../components/atoms/InputControlled';
import FormularioService from '../API/FormularioService';
import { useCloudflareScript } from '../hooks/useCloudflareScript';
import { CLOUD_FLARE_CAPTCHA, NONE_CAPTCHA } from '../constants/network';
import analyticsService from '../API/analyticsService';

const phoneRegExp = /^\+?[1-9]\d{1,14}$/;
const initialValues = {
  usrAnonimo: '',
  txtSituacion: '',
  tipoUsuario: '',
  usrNombre: '',
  usrTelefono: '',
  usrCorreo: '',
  genero: '',
};

const sortedErrors = ['usrAnonimo', 'usrNombre', 'tipoUsuario', 'usrTelefono', 'usrCorreo', 'genero', 'txtSituacion'];

export const DenunciaScreen = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { loaded, getToken, captchaType } = useCloudflareScript({ elementId: 'cf-turnstile', options: {} });

  const [files, setFiles] = useState([]);
  const [typeUsers, setTypeUsers] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [sucessDialog, setSuccessDialog] = useState({
    open: false,
    data: null,
    copyMessage: '',
  });

  const inputRef = useRef();

  const handleOpenSuccessDialog = data => {
    setSuccessDialog(prevState => ({
      ...prevState,
      open: true,
      data,
    }));
  };

  const onSubmit = async values => {
    analyticsService.sendEvent('BTN_ENVIAR_DENUNCIA');

    try {
      const captcha = await getToken('captcha_tescucha');

      if (!captcha && captchaType !== NONE_CAPTCHA) return;

      const formData = new FormData();

      formData.append(
        'denuncia',
        new Blob(
          [
            JSON.stringify({
              ...values,
              usrAnonimo: values.usrAnonimo !== 'No',
            }),
          ],
          { type: 'application/json' }
        )
      );

      files.forEach(file => {
        formData.append('files', file);
      });

      setOpenConfirmDialog(false);
      const { clave, folio } = await FormularioService.guardarFormulario({
        dispatch,
        showLoader: true,
        captcha: captcha || '',
        data: formData,
      });

      handleOpenSuccessDialog({ password: clave, folio });
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const onOpenFileDialog = () => {
    inputRef.current?.click();
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialog({
      open: false,
      data: null,
    });
    history.replace('/');
  };

  const onFilesSelected = event => {
    const selectedFiles = Array.from(event.target.files);
    setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
  };

  const onDeleteFile = index => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const onPreviewFile = index => {
    const file = files[index];
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, '_blank');
  };

  const {
    values,
    errors,
    dirty,
    isValid,
    touched,
    isSubmitting,
    handleSubmit,
    handleBlur,
    handleChange,
    setFieldValue,
  } = useFormik({
    initialValues,
    onSubmit,
    validationSchema: Yup.object().shape({
      usrAnonimo: Yup.string().required('Selecciona una opción'),
      txtSituacion: Yup.string()
        .required('La descripción es requerida')
        .min(50, 'La descripción debe tener al menos 50 caracteres'),
      tipoUsuario: Yup.string().when('usrAnonimo', {
        is: 'No',
        then: Yup.string().required('Selecciona una opción'),
        otherwise: Yup.string().notRequired(),
      }),
      usrNombre: Yup.string().when('usrAnonimo', {
        is: 'No',
        then: Yup.string().required('El nombre es requerido'),
        otherwise: Yup.string().notRequired(),
      }),
      usrTelefono: Yup.string().when('usrAnonimo', {
        is: 'No',
        then: Yup.string()
          .required('El teléfono es requerido')
          .matches(phoneRegExp, 'Ingresa un número de teléfono válido'),
        otherwise: Yup.string().notRequired().matches(phoneRegExp, 'Ingresa un número de teléfono válido'),
      }),
      usrCorreo: Yup.string()
        .email('Ingresa un correo electrónico válido')
        .when('usrAnonimo', {
          is: 'No',
          then: Yup.string().required('El correo electrónico es requerido'),
          otherwise: Yup.string().notRequired(),
        }),
      genero: Yup.string().when('usrAnonimo', {
        is: 'No',
        then: Yup.string().required('Selecciona una opción'),
        otherwise: Yup.string().notRequired(),
      }),
    }),
    validateOnMount: true,
  });

  const populateCatalogs = useCallback(async () => {
    try {
      const [tipoUsuario] = await Promise.all([CatalogoService.consultaCatalogo({ dispatch, path: 'tipo-usuario' })]);
      setTypeUsers(tipoUsuario);
    } catch (error) {
      console.error('Error fetching catalogs:', error);
    }
  }, []);

  const handleGoBack = () => {
    analyticsService.sendEvent('BTN_DENUNCIA_REGRESAR');
    history.replace('/');
  };

  const handleCopyToClipboard = async () => {
    if (!sucessDialog.data) return;
    analyticsService.sendEvent('BTN_DENUNCIA_COPIAR');

    const textToCopy = `Número de denuncia: ${sucessDialog.data.folio} Contraseña: ${sucessDialog.data.password}`;

    await navigator.clipboard.writeText(textToCopy);
    setSuccessDialog(prevState => ({
      ...prevState,
      copyMessage: '¡Copiado al portapapeles!',
    }));

    setTimeout(() => {
      setSuccessDialog(prevState => ({
        ...prevState,
        copyMessage: '',
      }));
    }, 3000);
  };

  useEffect(() => {
    populateCatalogs();
  }, [populateCatalogs]);

  /* a la escucha de usrAnonimo == 'Si' */
  useEffect(() => {
    if (values.usrAnonimo === 'Si') {
      ['tipoUsuario', 'usrNombre', 'usrTelefono', 'usrCorreo', 'genero'].forEach(field => {
        setFieldValue(field, '', false);
      });
      setOpenInfoDialog(true);
    }
  }, [values.usrAnonimo]);

  /* scroll al primer error */
  useEffect(() => {
    if (Object.keys(errors).length > 0 && isSubmitting) {
      const firstErrorField = sortedErrors.find(field => errors[field]);
      if (firstErrorField) {
        const element = document.getElementsByName(firstErrorField)[0];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }, [errors, isSubmitting]);

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h5" component="h1" className={classes.headerTitle}>
          Sistema de Denuncias TEscucha
        </Typography>
      </Box>
      <Typography className={classes.subtitle}>
        Gracias por confiar en nosotros. Tu denuncia será atendida con confidencialidad, imparcialidad y
        profesionalismo.
      </Typography>

      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionHeaderTitle}>Tipo de denuncia</Typography>
        </Box>
        <Box className={classes.sectionContent}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '1fr 3fr',
              },
              gridTemplateRows: 'auto',
              gap: 30,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gridGap: 10,
              }}
            >
              <FormControl component="fieldset">
                <FormLabel component="legend" className={classes.label}>
                  ¿Quieres que tu denuncia sea anónima?
                </FormLabel>
                <RadioGroup name="usrAnonimo" value={values.usrAnonimo} onChange={handleChange} onBlur={handleBlur} row>
                  <FormControlLabel
                    style={{ marginBottom: 0 }}
                    value="Si"
                    control={<Radio color="primary" size="small" />}
                    label="Sí"
                  />
                  <FormControlLabel
                    style={{ marginBottom: 0 }}
                    value="No"
                    control={<Radio color="primary" size="small" />}
                    label="No"
                  />
                </RadioGroup>
                {touched.usrAnonimo && errors.usrAnonimo && <FormHelperText error>{errors.usrAnonimo}</FormHelperText>}
              </FormControl>
              <Collapse in={values.usrAnonimo === 'No'} unmountOnExit>
                <InputControlled
                  label="Nombre(s) y apellidos"
                  name="usrNombre"
                  placeholder="Escribe tu nombre completo"
                  value={values.usrNombre}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.usrNombre && Boolean(errors.usrNombre)}
                  helperText={touched.usrNombre && errors.usrNombre}
                  fullWidth={false}
                />
              </Collapse>
            </Box>
            <Collapse in={values.usrAnonimo === 'No'} unmountOnExit>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gridGap: 10,
                }}
              >
                <FormControl component="fieldset">
                  <FormLabel component="legend" className={classes.label}>
                    Soy
                  </FormLabel>
                  <RadioGroup
                    name="tipoUsuario"
                    value={values.tipoUsuario}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    row
                  >
                    {typeUsers.map(typeUser => (
                      <FormControlLabel
                        key={typeUser.id}
                        style={{ marginBottom: 0 }}
                        value={`${typeUser.id}`}
                        control={<Radio color="primary" size="small" />}
                        label={typeUser.value}
                      />
                    ))}
                  </RadioGroup>
                  {touched.tipoUsuario && errors.tipoUsuario && (
                    <FormHelperText error>{errors.tipoUsuario}</FormHelperText>
                  )}
                </FormControl>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 30,
                  }}
                >
                  <InputControlled
                    label="Teléfono"
                    name="usrTelefono"
                    placeholder="Escribe aquí"
                    value={values.usrTelefono}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.usrTelefono && Boolean(errors.usrTelefono)}
                    helperText={touched.usrTelefono && errors.usrTelefono}
                    sxContainer={{
                      minWidth: 150,
                      width: {
                        xs: '100%',
                        md: 'auto',
                      },
                    }}
                  />
                  <InputControlled
                    label="Correo electrónico"
                    name="usrCorreo"
                    placeholder="nombre@correo.com.mx"
                    value={values.usrCorreo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.usrCorreo && Boolean(errors.usrCorreo)}
                    helperText={touched.usrCorreo && errors.usrCorreo}
                    sxContainer={{
                      minWidth: 250,
                      width: {
                        xs: '100%',
                        md: 'auto',
                      },
                    }}
                  />
                  <FormControl component="fieldset" error={touched.genero && Boolean(errors.genero)}>
                    <FormLabel component="legend" className={classes.label}>
                      Género
                    </FormLabel>
                    <RadioGroup name="genero" value={values.genero} onChange={handleChange} onBlur={handleBlur} row>
                      <FormControlLabel
                        style={{ marginBottom: 0 }}
                        value="Masculino"
                        control={<Radio color="primary" size="small" />}
                        label="Masculino"
                      />
                      <FormControlLabel
                        style={{ marginBottom: 0 }}
                        value="Femenino"
                        control={<Radio color="primary" size="small" />}
                        label="Femenino"
                      />
                    </RadioGroup>
                    {touched.genero && errors.genero && <FormHelperText>{errors.genero}</FormHelperText>}
                  </FormControl>
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Box>
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionHeaderTitle}>Describe tu denuncia</Typography>
        </Box>
        <Box className={classes.sectionContent}>
          <Typography className={classes.label} gutterBottom>
            Cuéntanos lo que ocurrió con el mayor detalle posible.
            <Typography className={classes.label}>
              Incluye información como: qué pasó, dónde y cuándo sucedió, quién o quiénes estuvieron involucrados, si
              hubo testigos u otras personas que sepan del caso.
            </Typography>
          </Typography>
          <TextField
            placeholder="Escribe aquí tu denuncia."
            name="txtSituacion"
            value={values.txtSituacion}
            onChange={handleChange}
            onBlur={handleBlur}
            multiline
            minRows={4}
            error={touched.txtSituacion && Boolean(errors.txtSituacion)}
            helperText={touched.txtSituacion && errors.txtSituacion}
            variant="outlined"
            fullWidth
          />
        </Box>
      </Box>
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionHeaderTitle}>Archivos de evidencia (opcional)</Typography>
        </Box>
        <Box className={classes.sectionContent}>
          <Typography className={classes.label} gutterBottom>
            Si quieres compartir archivos que consideres útiles para tu denuncia (documentos, fotos, videos, audios),
            puedes cargarlos aquí.
          </Typography>
          <Box className={classes.filesContainer}>
            <Box className={classes.fileItem}>
              <input
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                id="raised-button-file"
                multiple
                type="file"
                ref={inputRef}
                onChange={onFilesSelected}
                value=""
              />
              <Button
                variant="outlined"
                color="primary"
                component="span"
                className={classes.btnFile}
                startIcon={<UploadIcon size={16} />}
                onClick={onOpenFileDialog}
              >
                Cargar archivos
              </Button>
            </Box>
            {files.length > 0 ? (
              <Box className={classes.filesPreviewGrid}>
                {files.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexDirection: 'row',
                      width: '100%',
                      borderRadius: 4,
                      border: '1px solid #D8DCDF',
                      padding: '2px 8px',
                      height: 32,
                      maxHeight: 32,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'row',
                        gridGap: 2,
                        minWidth: 0,
                        flex: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <FileIcon color="#000" />
                      <Typography variant="body2" color="inherit" className={classes.fileName}>
                        {file.name}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'row',
                        flexShrink: 0,
                      }}
                    >
                      <Tooltip
                        title="Visualizar"
                        arrow
                        placement="top"
                        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                      >
                        <IconButton size="small" onClick={() => onPreviewFile(index)}>
                          <VisibilityOutlinedIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title="Eliminar"
                        arrow
                        placement="top"
                        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                      >
                        <IconButton size="small" onClick={() => onDeleteFile(index)}>
                          <DeleteOutlineIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box className={classes.filesPreview}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#979797',
                  }}
                >
                  <FileIcon />
                  <Typography variant="body2" color="inherit">
                    Aún no hay archivos adjuntos. (máximo 10MB)
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: captchaType === CLOUD_FLARE_CAPTCHA ? 'flex' : 'none',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div id="cf-turnstile" />
        </Box>
      </Box>
      <Box className={classes.actionsFooter}>
        <Footer />
        <Box className={classes.actionsButtons}>
          <Button
            variant="outlined"
            color="primary"
            style={{ backgroundColor: '#fff', fontSize: '16px' }}
            onClick={() => {
              setOpenConfirmDialog(true);
            }}
          >
            Regresar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disableElevation
            style={{
              minWidth: 177,
              backgroundColor: !(dirty && isValid && loaded) ? '#E6E9EA' : undefined,
              color: !(dirty && isValid && loaded) ? '#6F7A83' : undefined,
              fontWeight: 'bold',
              fontSize: '18px',
            }}
          >
            Enviar denuncia
          </Button>
        </Box>
      </Box>

      {/* DIALOGS */}
      <Dialog
        open={openInfoDialog}
        aria-labelledby="consult-dialog-title"
        aria-describedby="consult-dialog-description"
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        }}
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: 16,
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
          },
        }}
      >
        <DialogContent className={classes.successDialogContent}>
          <Typography variant="body1" className={classes.titleDialogAnonima}>
            Has elegido presentar tu denuncia de manera anónima.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Respetamos tu decisión. Ten en cuenta que, sin datos de contacto, el seguimiento del caso podría verse
            limitado.
          </Typography>

          <Box mt={2} display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenInfoDialog(false)}
              disableElevation
              style={{ minWidth: 102 }}
            >
              Continuar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openConfirmDialog}
        aria-labelledby="consult-dialog-title"
        aria-describedby="consult-dialog-description"
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        }}
        PaperProps={{
          style: {
            borderRadius: 16,
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
            maxWidth: '350px',
          },
        }}
      >
        <DialogContent className={classes.successDialogContent}>
          <Typography variant="body1" gutterBottom>
            Al dar clic en &quot;Aceptar&quot; se perderá la información que escribiste.
          </Typography>

          <Box mt={2} display="flex" justifyContent="flex-end" alignItems="center" sx={{ gap: 10 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOpenConfirmDialog(false)}
              disableElevation
              style={{ minWidth: 102, fontWeight: '500' }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGoBack}
              disableElevation
              style={{ minWidth: 102, fontWeight: 'bold' }}
            >
              Aceptar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={sucessDialog.open}
        aria-labelledby="consult-dialog-title"
        aria-describedby="consult-dialog-description"
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        }}
        PaperProps={{
          style: {
            borderRadius: 16,
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
            maxWidth: '700px',
          },
        }}
      >
        <DialogContent className={classes.successDialogContent}>
          <Typography variant="h6" className={classes.successDialogTitle}>
            Tu denuncia ha sido enviada.
          </Typography>
          <Typography variant="body1">
            Por favor anota y guarda el número de denuncia y la contraseña que aparecen a continuación:
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: 'row',
              },
              alignItems: 'center',
              justifyContent: 'center',
              gap: 20,
              border: '1px solid #D8DCDF',
              borderRadius: '8px',
              padding: '10px',
              marginY: '16px',
              cursor: 'pointer',
            }}
            onClick={handleCopyToClipboard}
          >
            <Typography variant="body1" className={classes.bold}>
              Número de denuncia:{' '}
              <Typography component="span" color="primary" className={classes.dataDenunciaText}>
                {sucessDialog.data?.folio}
              </Typography>
            </Typography>
            <Divider
              orientation="vertical"
              flexItem
              className={classes.divider}
            />
            <Typography variant="body1" className={classes.bold}>
              Contraseña:{' '}
              <Typography component="span" color="primary" className={classes.dataDenunciaText}>
                {sucessDialog.data?.password}
              </Typography>
            </Typography>
          </Box>
          <Collapse in={Boolean(sucessDialog.copyMessage)} unmountOnExit>
            <Box
              sx={{
                borderRadius: 8,
                backgroundColor: '#e6ffed',
                border: '1px solid #34c759',
                color: '#034d22',
                padding: '5px 10px',
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <CheckIcon fontSize="small" color="inherit" />
              {sucessDialog.copyMessage}
            </Box>
          </Collapse>
          <Typography>
            Estos datos son necesarios para que puedas acceder a la sección de “
            <Typography style={{ fontWeight: 'bold' }} component="span">
              Da seguimiento a una denuncia
            </Typography>
            ” que encontrarás en el menú principal de TEscucha.
          </Typography>

          <Box mt={2} display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleCloseSuccessDialog}
              disableElevation
              style={{ minWidth: 102 }}
            >
              Aceptar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  header: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, '16px'),
    borderRadius: 4,
  },
  headerTitle: {
    fontWeight: 'bold',
    textAlign: 'left',
  },
  subtitle: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  section: {
    borderRadius: 8,
    backgroundColor: '#fff',
    boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
    overflow: 'hidden',
  },
  sectionHeader: {
    color: '#fff',
    backgroundColor: '#273138',
    padding: theme.spacing('8px', '16px'),
  },
  sectionHeaderTitle: {
    fontWeight: '600',
    fontSize: '1rem',
  },
  sectionContent: {
    padding: theme.spacing('16px', '24px'),
  },
  label: {
    fontSize: '1rem',
    fontWeight: '500',
    color: '#000',
  },
  actionsFooter: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(0),
    flexWrap: 'wrap-reverse',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
    },
  },
  actionsButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-end',
    },
  },
  filesContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  fileItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    border: '1px solid #D8DCDF',
    height: 92,
  },
  btnFile: {
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    borderColor: '#2168D3',
    color: '#2168D3',
    textTransform: 'none',
    minWidth: 170,
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#E6F0FA',
      borderColor: '#2168D3',
    },
  },
  filesPreview: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxHeight: 92,
    height: 92,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    border: '1px solid #D8DCDF',
    padding: theme.spacing(1),
  },
  filesPreviewGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '32px',
    gap: '4px',
    overflowY: 'auto',
    width: '100%',
    maxHeight: 92,
    height: 92,
    overflowX: 'hidden',
    minWidth: 0,
  },
  fileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    minWidth: 0,
  },
  successDialogContent: {
    padding: '24px',
  },
  successDialogTitle: {
    fontWeight: 'bold',
    fontSize: '24px',
    marginBottom: '12px',
  },
  bold: { fontWeight: 'bold' },
  tooltip: {
    backgroundColor: '#FAFBFE',
    color: '#232322',
    fontSize: '0.875rem',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
    padding: '8px',
    fontFamily: 'Roboto Condensed, sans-serif',
    fontWeight: '300',
  },
  tooltipArrow: {
    color: '#FAFBFE',
    '&::before': {
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
    },
  },
  medium: { fontWeight: '500' },
  dataDenunciaText: { fontWeight: 'bold', fontSize: '20px' },
  titleDialogAnonima: {
    fontWeight: 'bold',
    marginBottom: '12px',
  },
  divider: {
    backgroundColor: '#5F6368',
  }
}));
